import type { TestSummary } from './tests';
import { fold, type SortDirection } from './sorting';

/**
 * Finding and ordering the library.
 *
 * In the browser: a centre's papers arrive in one response and a library is
 * counted in dozens, not thousands. The library is also read in three sections
 * — one per module, in the order a candidate sits them — so ordering happens
 * inside each section rather than across all of them, which keeps a search for
 * "Test 2" from mixing a Listening paper into the Reading list.
 */
export type PaperSortKey = 'title' | 'variant' | 'status' | 'questions' | 'imported';

export type Shelf = 'CURRENT' | 'PAST';

/**
 * Which shelf a paper sits on.
 *
 * The shelf was added to libraries that already existed, so a paper imported
 * before it has none — and a paper a centre has been teaching from for a year
 * is not a past paper, so the absence reads as current. Any value that is not
 * PAST is read the same way: a shelf nobody set is not a reason to hide a
 * paper from the shelf teachers actually look at.
 */
export const shelfOf = (paper: TestSummary): Shelf => (paper.shelf === 'PAST' ? 'PAST' : 'CURRENT');

/** The papers kept on one shelf, in the order they arrived in. */
export const onShelf = (papers: TestSummary[], shelf: Shelf): TestSummary[] =>
  papers.filter((paper) => shelfOf(paper) === shelf);

/** How many papers are on a shelf — for the chip that offers to show it. */
export const countShelf = (papers: TestSummary[], shelf: Shelf): number =>
  papers.reduce((total, paper) => total + (shelfOf(paper) === shelf ? 1 : 0), 0);

/**
 * Title, whole test, or status.
 *
 * The status is worth searching because "draft" is a question a teacher asks —
 * what have I started and not finished? — and it is a word on the screen in
 * front of them, so typing it should do something.
 */
export function searchPapers(papers: TestSummary[], query: string): TestSummary[] {
  const needle = fold(query);
  if (!needle) return papers;
  return papers.filter(
    (paper) =>
      fold(paper.title).includes(needle) ||
      fold(paper.setName ?? '').includes(needle) ||
      fold(paper.status).includes(needle),
  );
}

/**
 * Not alphabetical: a library sorted by status is being asked what needs work.
 * Drafts first, then the papers in use, then the ones taken out of circulation.
 */
const STATUS_RANK: Record<string, number> = { DRAFT: 0, PUBLISHED: 1, ARCHIVED: 2 };

const compare = (a: TestSummary, b: TestSummary, key: PaperSortKey): number => {
  switch (key) {
    case 'title':
      return a.title.localeCompare(b.title);
    case 'variant':
      return a.variant.localeCompare(b.variant);
    case 'status':
      return (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9);
    case 'questions':
      return a.questionCount - b.questionCount;
    case 'imported':
      return a.createdAt.localeCompare(b.createdAt);
  }
};

export function sortPapers(
  papers: TestSummary[],
  key: PaperSortKey,
  direction: SortDirection,
): TestSummary[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...papers].sort((a, b) => {
    const first = compare(a, b, key);
    // Papers tie on status and on variant constantly; the title underneath
    // keeps a section from reshuffling every time it is re-sorted.
    return first !== 0 ? first * sign : a.title.localeCompare(b.title);
  });
}

export const arrangePapers = (
  papers: TestSummary[],
  options: { query: string; key: PaperSortKey; direction: SortDirection },
): TestSummary[] => sortPapers(searchPapers(papers, options.query), options.key, options.direction);
